import { useMemo, useCallback } from "react";
import { KitPermission, SpacePermission, ItemType, AssetType } from "@thenounproject/lingo-core";
import { Inspectable } from "@constants/Inspector";
import useNavPoint from "@hooks/useNavPoint";
import {
  ActionContext,
  LocationContext,
  CapabilitiesContext,
  SelectionContext,
} from "./registry/types";
import { useViewModeContext } from "@src/contexts/ViewModeProvider";
import { useGetSections } from "@src/@redux/selectors/getters";

type UseActionContextOptions = {
  inspectables: Inspectable[];
  userInput?: string;
  onEditItem?: (itemId: string) => void;
};

/**
 * Computes the selection context from inspectables by filtering into categories
 */
function computeSelectionContext(inspectables: Inspectable[]): SelectionContext {
  const items = inspectables.filter(i => i.item),
    assets = inspectables.filter(i => i.asset),
    assetItems = items.filter(i => i.item?.type === ItemType.asset),
    noteItems = items.filter(i => i.item?.type === ItemType.note),
    guideItems = items.filter(i => i.item?.type === ItemType.guide),
    headingItems = items.filter(i => i.item?.type === ItemType.heading),
    galleryItems = items.filter(i => i.item?.type === ItemType.gallery),
    codeItems = items.filter(i => i.item?.type === ItemType.codeSnippet),
    motionAssets = items.filter(i => AssetType.motionTypes.has(i.asset?.type)),
    resizableItems = items.filter(i =>
      [ItemType.asset, ItemType.guide, ItemType.gallery, ItemType.note].includes(i.item.type)
    ),
    cardBackgroundCompatibleItems = inspectables.filter(
      i => i.item && ![ItemType.heading].includes(i.item?.type)
    );

  return {
    all: inspectables,
    items,
    assets,
    assetItems,
    noteItems,
    guideItems,
    headingItems,
    galleryItems,
    codeItems,
    motionAssets,
    resizableItems,
    cardBackgroundCompatibleItems,
  };
}

/**
 * Hook that provides a memoized ActionContext for use in menus
 */
export function useActionContext({
  inspectables,
  userInput = "",
}: UseActionContextOptions): ActionContext {
  const getContext = useActionContextCallback({ inspectables, userInput });

  return useMemo(() => {
    return getContext();
  }, [getContext]);
}

/**
 * Hook that provides a callback to lazily compute ActionContext
 * Useful for keyboard shortcuts where context should be computed on-demand
 */
export function useActionContextCallback({
  inspectables,
  userInput,
}: UseActionContextOptions): () => ActionContext | null {
  const navPoint = useNavPoint();
  const { inspectorMode } = useViewModeContext();
  const sections = useGetSections();

  return useCallback((): ActionContext | null => {
    const { kit, kitVersion, space, portal, type, gallery } = navPoint;
    if (!type) return null;

    // Compute location context
    const location: LocationContext = {
      type,
      inLibrary: type === "Library",
      inAssetDetail: type === "LibraryAsset" || type === "Item",
      inGallery: type === "Gallery",
      inKit: Boolean(kit),
      kit,
      gallery,
      space,
      portal,
    };

    const canEdit = (() => {
      if (kitVersion?.version > 0) return false;
      return (
        kit?.access?.permissions.includes(KitPermission.editContent) ||
        space?.access?.permissions.includes(SpacePermission.edit)
      );
    })();
    const capabilities: CapabilitiesContext = { canEdit };

    // Compute selection context
    const selection = computeSelectionContext(inspectables);

    return {
      userInput,
      location,
      capabilities,
      selection,
      data: {
        sections,
      },
      interface: {
        inspectorMode,
      },
    };
  }, [navPoint, inspectables, userInput, sections, inspectorMode]);
}
